import React, { useState, useEffect } from "react";
import { CheckCircle, ArrowLeft, Trash2, Download, Share2, Send, Clock, AlertCircle, ChevronDown, Info, UserCheck, Eye, Users, X } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Sidebar from "./Sidebar";
import Header from "./Header";
import { request } from "../service/HttpService";
import { deleteSubmissionCall, downloadManuscriptCall, exportDownloadManuscriptCall, shareReportCall } from "../service/ManuscriptService";
import { errorMessage } from "../service/HttpErrors";
import { fetchPeerReviewByManuscript, submitPeerReview, cancelPeerReview, fetchReviewFeedback } from "../service/PeerReviewService";
import ReviewFeedbackView from "./ReviewFeedbackView";
import { calculateOverallScore, sectionColor } from "../service/Operations";
import { PEER_REVIEW_ENABLED } from "../constants/Constants";
import NetworkCanvas from "./NetworkCanvas";
import ScoreDistribution from "./ScoreDistribution";

const networkNodes = [
    { id: "your", label: "Your Paper", x: 46, y: 48, size: 14, highlight: true },
    { id: "a", x: 22, y: 28, size: 8 },
    { id: "b", x: 68, y: 22, size: 9 },
    { id: "c", x: 75, y: 58, size: 7 },
    { id: "d", x: 30, y: 68, size: 8 },
    { id: "e", x: 55, y: 78, size: 6 },
    { id: "f", x: 14, y: 52, size: 6 },
    { id: "g", x: 60, y: 38, size: 7 },
    { id: "h", x: 38, y: 18, size: 6 },
];

const networkEdges = [
    ["your", "a"], ["your", "b"], ["your", "c"], ["your", "d"],
    ["your", "g"], ["a", "f"], ["a", "d"], ["b", "g"], ["b", "h"],
    ["c", "e"], ["d", "e"],
];

function NetworkGraph() {
    const nodeMap = Object.fromEntries(networkNodes.map(n => [n.id, n]));
    return (
        <div className="network-graph-wrap">
            <svg className="network-svg" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet">
                {networkEdges.map(([a, b], i) => (
                    <line
                        key={i}
                        x1={nodeMap[a].x} y1={nodeMap[a].y}
                        x2={nodeMap[b].x} y2={nodeMap[b].y}
                        stroke="rgba(160,220,180,0.25)"
                        strokeWidth="0.5"
                    />
                ))}
                {networkNodes.map(n => (
                    <circle
                        key={n.id}
                        cx={n.x} cy={n.y} r={n.size / 2}
                        fill={n.highlight ? "#5ecfa0" : "rgba(160,220,180,0.55)"}
                    />
                ))}
                <text x={46} y={43} textAnchor="middle" fontSize="3.5" fill="white" fontWeight="bold">Your Paper</text>
            </svg>
        </div>
    );
}

function ReferenceList({ references }) {
    // The works the reviewers cited, numbered once across all three dimensions — the same
    // list, in the same order, that the exported report PDF prints.
    const [open, setOpen] = useState(false);
    if (!references.length) return null;
    return (
        <div className="submissions-card">
            <button className="analysis-group-header" onClick={() => setOpen(o => !o)}>
                <span>References</span>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span className="analysis-group-count">
                        {references.length} {references.length === 1 ? "source" : "sources"}
                    </span>
                    <ChevronDown size={15} className={`analysis-chevron ${open ? "analysis-chevron-open" : ""}`} />
                </div>
            </button>
            {open && (
                <div className="analysis-items">
                    {references.map(ref => {
                        const tail = [ref.venue, ref.year].filter(Boolean).join(", ");
                        const href = ref.doi ? `https://doi.org/${ref.doi}` : ref.url;
                        return (
                            <div key={ref.refNumber} className="analysis-item">
                                <p className="analysis-issue">
                                    <span className="analysis-refs-number">{ref.refNumber}.</span>{" "}
                                    {ref.title}
                                </p>
                                {tail && <p className="analysis-suggestion">{tail}</p>}
                                {href && (
                                    <a className="analysis-suggestion" href={href}
                                       target="_blank" rel="noopener noreferrer">
                                        {ref.doi ? `doi:${ref.doi}` : ref.url}
                                    </a>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

function AnalysisGroups({ grouped, labelMap }) {
    const [open, setOpen] = useState({});
    const toggle = (key) => setOpen(s => ({ ...s, [key]: !s[key] }));
    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {Object.entries(grouped).map(([scoreType, items]) => {
                const isOpen = !!open[scoreType];
                return (
                    <div key={scoreType} className="submissions-card">
                        <button className="analysis-group-header" onClick={() => toggle(scoreType)}>
                            <span>{labelMap[scoreType] ?? scoreType} Analysis</span>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <span className="analysis-group-count">{items.length} {items.length === 1 ? "item" : "items"}</span>
                                <ChevronDown size={15} className={`analysis-chevron ${isOpen ? "analysis-chevron-open" : ""}`} />
                            </div>
                        </button>
                        {isOpen && (
                            <div className="analysis-items">
                                {items.map((item, i) => (
                                    <div key={item.id ?? i} className="analysis-item">
                                        {item.severity && (
                                            <span className={`analysis-severity severity-${item.severity.toLowerCase()}`}>
                                                {item.severity.charAt(0).toUpperCase() + item.severity.slice(1).toLowerCase()} Priority
                                            </span>
                                        )}
                                        <p className="analysis-issue">
                                            {item.issue}
                                            {item.refNumbers && (
                                                <sup className="analysis-refs">{item.refNumbers}</sup>
                                            )}
                                        </p>
                                        {item.quote && <blockquote className="analysis-quote">{item.quote}</blockquote>}
                                        {item.suggestion && <p className="analysis-suggestion">{item.suggestion}</p>}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

export default function Submission() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const id = searchParams.get("id");
    const [paper, setPaper] = useState(null);
    const [existsInS3, setExistsInS3] = useState(false);
    const [hasGraph, setHasGraph] = useState(false);
    const [hasDeepThinkers, setHasDeepThinkers] = useState(false);
    const [similarPapers, setSimilarPapers] = useState([]);
    const [topDeepThinkers, setTopDeepThinkers] = useState([]);
    const [deepThinkersLoading, setDeepThinkersLoading] = useState(true);
    const [loading, setLoading] = useState(true);
    const [similarLoading, setSimilarLoading] = useState(true);
    const [downloading, setDownloading] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [shareModal, setShareModal] = useState(false);
    const [shareEmail, setShareEmail] = useState("");
    const [exporting, setExporting] = useState(false);
    const [sharing, setSharing] = useState(false);
    const [shareStatus, setShareStatus] = useState(null); // { type: "success"|"error", msg }

    const [analyses, setAnalyses] = useState([]);
    const [references, setReferences] = useState([]);
    const [distData, setDistData] = useState(null);

    // Peer review state
    const [peerReview, setPeerReview] = useState(null);
    const [hasActiveReview, setHasActiveReview] = useState(false);
    const [prLoading, setPrLoading] = useState(true);
    const [prSubmitting, setPrSubmitting] = useState(false);
    const [prNotes, setPrNotes] = useState("");
    const [prModal, setPrModal] = useState(false);
    const [prStatus, setPrStatus] = useState(null);
    const [prCancelling, setPrCancelling] = useState(false);
    const [prFeedback, setPrFeedback] = useState(null);

    const [smallScreen, setSmallScreen] = useState(window.innerWidth < 991);

    useEffect(() => {
        const onResize = () => {
            const small = window.innerWidth < 991;
            setSmallScreen(small);
            if (small) setLoading(false);
        };
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    useEffect(() => {
        if (id) {
            request("GET", `/manuscript/${id}`)
                .then(res => {
                    if (res.manuscript) setPaper(res.manuscript);
                    setExistsInS3(res.existsInS3 ?? false);
                    setHasGraph(!!res.manuscript?.graphKey?.trim())
                    setHasDeepThinkers(!!res.manuscript?.deepThinkersKey?.trim())
                    if (res.manuscript?.status?.toLowerCase() === "completed") {
                        request("GET", `/analysis/all/${id}`)
                            .then(r => {
                                setAnalyses(r.analyses ?? []);
                                setReferences(r.references ?? []);
                            })
                            .catch(() => {});
                        request("GET", `/analytics/percentile/${id}`)
                            .then(setDistData)
                            .catch(() => {});
                    }
                    if (res.hasSimilarPapers) {
                        request("GET", `/similar/top/papers/${id}`)
                            .then(r => setSimilarPapers(r.similarPapers ?? []))
                            .catch(() => {})
                            .finally(() => setSimilarLoading(false));
                    } else {
                        setSimilarLoading(false);
                    }
                    if (res.manuscript?.deepThinkersKey?.trim()) {
                        request("GET", `/deepthinkers/${id}`)
                            .then(r => setTopDeepThinkers((r.deepThinkers ?? []).slice(0, 5)))
                            .catch(() => {})
                            .finally(() => setDeepThinkersLoading(false));
                    } else {
                        setDeepThinkersLoading(false);
                    }
                })
                .catch(() => {})
                .finally(() => setLoading(false));

            fetchPeerReviewByManuscript(id)
                .then(res => {
                    const review = res.review ?? null;
                    setPeerReview(review);
                    setHasActiveReview(res.hasActiveReview ?? false);
                    if (review && review.status === "reviewCompleted") {
                        fetchReviewFeedback(review.id)
                            .then(fbRes => { if (fbRes.success) setPrFeedback(fbRes.feedback); })
                            .catch(() => {});
                    }
                })
                .catch(() => {})
                .finally(() => setPrLoading(false));
        } else {
            setLoading(false);
            setPrLoading(false);
        }
    }, [id]);

    const handleDownload = async () => {
        setDownloading(true);
        try {
            const { blob, disposition } = await downloadManuscriptCall(id, true);
            const filename = disposition?.split("filename=")[1]?.replace(/"/g, "") || paper?.name || "manuscript.pdf";
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            a.click();
            URL.revokeObjectURL(url);
        } catch {
        } finally {
            setDownloading(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm("Are you sure you want to delete this manuscript? This cannot be undone.")) return;
        setDeleting(true);
        try {
            await deleteSubmissionCall(id, true);
            navigate("/submissions");
        } catch {
            setDeleting(false);
        }
    };

    const handleExportDownload = async () => {
        setExporting(true);
        try {
            const { blob, disposition } = await exportDownloadManuscriptCall(id, true);
            const filename = disposition?.split("filename=")[1]?.replace(/"/g, "").trim() || paper?.name || "report.pdf";
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = filename; a.click();
            URL.revokeObjectURL(url);
        } catch {
        } finally {
            setExporting(false);
        }
    };

    const handleShare = async (e) => {
        e.preventDefault();
        setSharing(true);
        setShareStatus(null);
        try {
            const res = await shareReportCall(id, shareEmail, true);
            if (res.success) {
                setShareStatus({ type: "success", msg: `Report sent to ${shareEmail}.` });
                setShareEmail("");
            } else {
                setShareStatus({ type: "error", msg: "Could not share report. Please try again." });
            }
        } catch (err) {
            setShareStatus({ type: "error", msg: errorMessage(err, "Could not share report. Please try again.") });
        } finally {
            setSharing(false);
        }
    };

    const handlePrSubmit = async () => {
        setPrSubmitting(true);
        setPrStatus(null);
        try {
            const res = await submitPeerReview(id, prNotes || null);
            if (res.success) {
                setPrStatus({ type: "success", msg: "Submitted for peer review." });
                setPeerReview(res.review);
                setHasActiveReview(true);
                setPrNotes("");
                setTimeout(() => { setPrModal(false); setPrStatus(null); }, 1500);
            } else {
                setPrStatus({ type: "error", msg: res.message || "Failed to submit." });
            }
        } catch {
            setPrStatus({ type: "error", msg: "Failed to submit. Please try again." });
        } finally {
            setPrSubmitting(false);
        }
    };

    const handlePrCancel = async () => {
        if (!peerReview || !window.confirm("Cancel this peer review request?")) return;
        setPrCancelling(true);
        try {
            const res = await cancelPeerReview(peerReview.id);
            if (res.success) {
                setPeerReview(null);
                setHasActiveReview(false);
            }
        } catch {}
        finally { setPrCancelling(false); }
    };

    const prStatusConfig = {
        awaitingReviewer: { label: "Awaiting Reviewer", icon: Clock, cls: "badge-pr-awaiting" },
        assigned:         { label: "Assigned",          icon: UserCheck, cls: "badge-pr-assigned" },
        inReview:         { label: "In Review",         icon: Eye, cls: "badge-pr-inreview" },
        reviewCompleted:  { label: "Completed",         icon: CheckCircle, cls: "badge-pr-completed" },
    };

    const [openTooltip, setOpenTooltip] = useState(null);
    const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });

    useEffect(() => {
        if (!openTooltip) return;
        const close = () => setOpenTooltip(null);
        document.addEventListener("click", close);
        return () => document.removeEventListener("click", close);
    }, [openTooltip]);

    const handleInfoClick = (e, label) => {
        e.stopPropagation();
        if (openTooltip === label) { setOpenTooltip(null); return; }
        const rect = e.currentTarget.getBoundingClientRect();
        const tooltipWidth = 240;
        let left = rect.left + rect.width / 2 - tooltipWidth / 2;
        left = Math.max(12, Math.min(left, window.innerWidth - tooltipWidth - 12));
        setTooltipPos({ top: rect.bottom + 8, left });
        setOpenTooltip(label);
    };

    const scoreDescriptions = {
        Novelty: "How much new ground your paper breaks compared to existing literature. Combines measures of bridging between communities, structural uniqueness, and disruption potential in the knowledge graph.",
        Methodology: "How well your paper employs and describes its methodological approach, including computational rigor, experimental techniques, and reproducibility. Measured by your paper's connectivity to method-related nodes in the research network.",
        Impact: "The potential reach and significance of your research contribution. Based on network position, citation patterns, community influence, and how centrally your paper sits in the research landscape.",
        Overall: "A combined score averaged across novelty, methodology, and impact. Reflects the holistic strength of your manuscript as evaluated by the research network analysis.",
    };

    const scoreCards = paper ? [
        { label: "Novelty", value: paper.noveltyScore },
        { label: "Methodology", value: paper.methodScore },
        { label: "Impact", value: paper.impactScore },
        { label: "Overall", value: calculateOverallScore(paper) },
    ] : [];

    return (
        <div className="home-layout">
            <Sidebar activePage="Submissions" />

            <main className="home-main">
                <Header />

                <div className="home-content">
                        <div className="upload-main">
                            <button className="chpw-back" onClick={() => navigate("/submissions")}>
                                <ArrowLeft size={14} />
                                Back to Submissions
                            </button>
                            <div className="upload-page-title-row">
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    {loading ? (
                                        <div className="skeleton" style={{ height: 22, width: "60%", marginBottom: 8 }} />
                                    ) : (
                                        <>
                                            <h1 className="upload-page-heading">{paper?.title || paper?.name}</h1>
                                            {paper?.authors && <p className="upload-page-meta">{paper.authors}</p>}
                                            {paper?.uploadDate && (
                                                <p className="submission-upload-date">
                                                    Uploaded {new Date(paper.uploadDate).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
                                                </p>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>

                            {(() => {
                                if (loading) return (
                                    <div className="submission-scores">
                                        {Array.from({ length: 4 }).map((_, i) => (
                                            <div key={i} className="submission-score-card">
                                                <div className="skeleton" style={{ height: 11, width: "55%", margin: "0 auto 10px" }} />
                                                <div className="skeleton" style={{ height: 32, width: "42%", margin: "0 auto 8px" }} />
                                            </div>
                                        ))}
                                    </div>
                                );

                                const status = paper?.status?.toLowerCase();
                                const isCompleted = status === "completed";
                                const isError = status === "error";

                                if (isError) return (
                                    <>
                                        <div className="sub-status-banner sub-status-error">
                                            <AlertCircle size={18} />
                                            <div>
                                                <p className="sub-status-title">Processing failed</p>
                                                <p className="sub-status-msg">There was an error processing your manuscript. Please try deleting and re-uploading.</p>
                                            </div>
                                        </div>
                                        <div className="sub-actions-row">
                                            {existsInS3 && (
                                                <button className="sub-action-btn sub-action-outline" onClick={handleDownload} disabled={downloading}>
                                                    <Download size={13} />
                                                    {downloading ? "Downloading..." : "Download Submission"}
                                                </button>
                                            )}
                                        </div>
                                    </>
                                );

                                if (!isCompleted) return (
                                    <>
                                        <div className="sub-status-banner sub-status-processing">
                                            <Clock size={18} />
                                            <div>
                                                <p className="sub-status-title">Analysis in progress</p>
                                                <p className="sub-status-msg">Your manuscript is being analyzed. This may take a few minutes — check back shortly.</p>
                                            </div>
                                        </div>
                                        {existsInS3 && (
                                            <div className="sub-actions-row">
                                                <button className="sub-action-btn sub-action-outline" onClick={handleDownload} disabled={downloading}>
                                                    <Download size={13} />
                                                    {downloading ? "Downloading..." : "Download Submission"}
                                                </button>
                                            </div>
                                        )}
                                    </>
                                );

                                const analysisOrder = ["novelty", "methods", "impact"];
                                const analysisLabel = { novelty: "Novelty", methods: "Methodology", impact: "Impact" };
                                const analysisGrouped = analysisOrder.reduce((acc, type) => {
                                    const items = analyses.filter(a => a.scoreType === type);
                                    if (items.length > 0) acc[type] = items;
                                    return acc;
                                }, {});

                                return (
                                    <>
                                        <div className="submission-scores">
                                            {scoreCards.map((s) => (
                                                <div key={s.label} className="submission-score-card">
                                                    <div className="submission-score-label-row">
                                                        {scoreDescriptions[s.label] && <span style={{ width: 11, flexShrink: 0 }} />}
                                                        <p className="submission-score-label">{s.label}</p>
                                                        {scoreDescriptions[s.label] && (
                                                            <button className="score-info-btn" onClick={e => handleInfoClick(e, s.label)}>
                                                                <Info size={11} />
                                                            </button>
                                                        )}
                                                    </div>
                                                    <p className="submission-score-value">{s.value}</p>
                                                </div>
                                            ))}
                                        </div>
                                        <div className="sub-actions-row">
                                            {existsInS3 && (
                                                <button className="sub-action-btn sub-action-outline" onClick={handleDownload} disabled={downloading}>
                                                    <Download size={13} />
                                                    {downloading ? "Downloading..." : "Download Submission"}
                                                </button>
                                            )}
                                            <button className="sub-action-btn sub-action-outline" onClick={handleExportDownload} disabled={exporting}>
                                                <Share2 size={13} />
                                                {exporting ? "Downloading..." : "Export Report"}
                                            </button>
                                            <button className="sub-action-btn sub-action-outline" onClick={() => { setShareModal(true); setShareStatus(null); setShareEmail(""); }}>
                                                <Send size={13} />
                                                Share Report
                                            </button>
                                            {PEER_REVIEW_ENABLED && existsInS3 && !prLoading && !hasActiveReview && !peerReview && (
                                                <button className="sub-action-btn sub-action-outline" onClick={() => { setPrModal(true); setPrStatus(null); setPrNotes(""); }}>
                                                    <Users size={13} />
                                                    Submit for Peer Review
                                                </button>
                                            )}
                                            {PEER_REVIEW_ENABLED && existsInS3 && !prLoading && (hasActiveReview || peerReview) && (
                                                <button className="sub-action-btn sub-action-outline" onClick={() => navigate("/peer-reviews")} style={{ color: "var(--series-blue)" }}>
                                                    <Users size={13} />
                                                    View Peer Review
                                                </button>
                                            )}
                                        </div>
                                        <p className="beta-disclaimer">This product is in beta. Scores and analysis are experimental and may not fully reflect the quality of your work.</p>

                                        {Object.keys(analysisGrouped).length > 0 && (
                                            <AnalysisGroups grouped={analysisGrouped} labelMap={analysisLabel} />
                                        )}
                                        <ReferenceList references={references} />
                                    </>
                                );
                            })()}

                            {/* How your paper compares — only for completed papers */}
                            {!loading && paper?.status?.toLowerCase() === "completed" && distData && (
                                <ScoreDistribution data={distData} />
                            )}

                            {/* Research Network — only for completed papers */}
                            {!loading && paper?.status?.toLowerCase() === "completed" && <div className="submissions-card">
                                {hasGraph && !smallScreen &&
                                    <>
                                <p className="similar-heading" style={{ padding: "16px 18px 0" }}>Research Network</p>
                                <div
                                    className="submission-network-graph-full submission-network-clickable"
                                    onClick={() => navigate(`/network?id=${id}`)}
                                    title="View full network"
                                >
                                    <div style={{ borderRadius: "12px", height: "25vh", position: "relative", background: "#0f1a14", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                        <NetworkCanvas />
                                        <div className="submission-network-view-hint">View Full Network →</div>
                                    </div>
                                </div>
                                </>
                                }

                                {/* Top DeepThinkers */}
                                {hasDeepThinkers && (
                                    <>
                                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 18px 10px" }}>
                                            <p className="similar-heading" style={{ padding: 0 }}>Top DeepThinkers</p>
                                            <button
                                                onClick={() => navigate(`/deepthinkers?id=${id}`)}
                                                style={{ background: "none", border: "none", color: "var(--ink-400)", fontSize: 12, cursor: "pointer", padding: 0 }}
                                            >
                                                View All →
                                            </button>
                                        </div>
                                        <table className="submissions-table similar-works-table">
                                            <thead>
                                                <tr>
                                                    <th>Researcher</th>
                                                    <th>Papers</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {deepThinkersLoading ? (
                                                    Array.from({ length: 3 }).map((_, i) => (
                                                        <tr key={i}>
                                                            <td>
                                                                <div className="skeleton" style={{ height: 12, width: "65%", marginBottom: 7 }} />
                                                                <div className="skeleton" style={{ height: 10, width: "40%" }} />
                                                            </td>
                                                            <td><div className="skeleton" style={{ height: 12, width: 24, borderRadius: 6 }} /></td>
                                                        </tr>
                                                    ))
                                                ) : topDeepThinkers.length > 0 ? topDeepThinkers.map((dt, i) => (
                                                    <tr key={i} className="submission-row" style={{ cursor: "pointer" }} onClick={() => navigate(`/deepthinkers?id=${id}`)}>
                                                        <td>
                                                            <p style={{ margin: 0, fontWeight: 500, color: "var(--ink-900)" }}>{dt.name}</p>
                                                        </td>
                                                        <td className="similar-works-pct">{dt.paperCount}</td>
                                                    </tr>
                                                )) : (
                                                    <tr><td colSpan={2}><p className="settings-empty">No DeepThinkers found.</p></td></tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </>
                                )}
                            </div>}

                            {!loading && paper?.status?.toLowerCase() === "completed" && <div className="submissions-card">
                                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 18px 10px" }}>
                                    <p className="similar-heading" style={{ padding: 0 }}>Top Similar Papers</p>
                                    <button
                                        onClick={() => navigate(`/similar-works?id=${id}`)}
                                        style={{ background: "none", border: "none", color: "var(--ink-400)", fontSize: 12, cursor: "pointer", padding: 0 }}
                                    >
                                        View All →
                                    </button>
                                </div>
                                <table className="submissions-table similar-works-table">
                                    <thead>
                                        <tr>
                                            <th>Title</th>
                                            <th style={{ whiteSpace: "nowrap" }}>% Overlap</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {similarLoading ? (
                                            Array.from({ length: 3 }).map((_, i) => (
                                                <tr key={i}>
                                                    <td>
                                                        <div className="skeleton" style={{ height: 12, width: "75%", marginBottom: 7 }} />
                                                        <div className="skeleton" style={{ height: 10, width: "50%" }} />
                                                    </td>
                                                    <td><div className="skeleton" style={{ height: 12, width: 36, borderRadius: 6 }} /></td>
                                                </tr>
                                            ))
                                        ) : similarPapers.length > 0 ? similarPapers.map((p) => (
                                            <tr key={p.id} className="submission-row" style={{ cursor: "pointer" }} onClick={() => navigate(`/similar-works?id=${id}`)}>
                                                <td>
                                                    <p className="similar-works-title-link" style={{ margin: 0 }}>{p.articleTitle}</p>
                                                    {p.section && (
                                                        <div style={{ marginTop: 4 }}>
                                                            <span className="similar-works-section-badge" style={sectionColor(p.section)}>{p.section}</span>
                                                        </div>
                                                    )}
                                                    {p.authors && <p className="submission-authors" style={{ marginTop: 4 }}>{p.authors}</p>}
                                                </td>
                                                <td className="similar-works-pct">{Math.round(p.similarity * 100)}%</td>
                                            </tr>
                                        )) : (
                                            <tr><td colSpan={2}><p className="settings-empty">No similar papers found.</p></td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>}

                            {/* Peer Review Status Card — only shown when a review exists */}
                            {!loading && !prLoading && peerReview && (
                                <div className="submissions-card pr-submission-card">
                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 18px 10px" }}>
                                        <p className="similar-heading" style={{ padding: 0 }}>Peer Review</p>
                                        <button
                                            onClick={() => navigate("/peer-reviews")}
                                            style={{ background: "none", border: "none", color: "var(--ink-400)", fontSize: 12, cursor: "pointer", padding: 0 }}
                                        >
                                            View All Reviews →
                                        </button>
                                    </div>
                                    <div style={{ padding: "0 18px 16px" }}>
                                        <div className="pr-status-card">
                                            <div className="pr-status-row">
                                                <span className={`status-badge ${prStatusConfig[peerReview.status]?.cls || "badge-pr-awaiting"}`}>
                                                    {React.createElement(prStatusConfig[peerReview.status]?.icon || Clock, { size: 12 })}
                                                    {prStatusConfig[peerReview.status]?.label || peerReview.status}
                                                </span>
                                                <span className="pr-status-date">
                                                    Submitted {new Date(peerReview.submittedDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                                                </span>
                                            </div>
                                            {peerReview.reviewerName && (
                                                <div className="pr-reviewer-info">
                                                    <UserCheck size={13} color="var(--series-blue)" />
                                                    <div>
                                                        <p className="pr-reviewer-name">{peerReview.reviewerName}</p>
                                                        {peerReview.reviewerInstitution && <p className="pr-reviewer-inst">{peerReview.reviewerInstitution}</p>}
                                                    </div>
                                                </div>
                                            )}
                                            {peerReview.notes && (
                                                <p className="pr-notes-display">Notes: {peerReview.notes}</p>
                                            )}
                                            {prFeedback && peerReview.status === "reviewCompleted" && (
                                                <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid var(--border)" }}>
                                                    <p style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>Review Feedback</p>
                                                    <ReviewFeedbackView feedback={prFeedback} />
                                                </div>
                                            )}
                                            {peerReview.status !== "reviewCompleted" && (
                                                <button
                                                    className="sub-action-btn sub-action-danger"
                                                    style={{ marginTop: 10 }}
                                                    onClick={handlePrCancel}
                                                    disabled={prCancelling}
                                                >
                                                    <X size={13} />
                                                    {prCancelling ? "Cancelling..." : "Cancel Review"}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Delete — always at the bottom */}
                            {!loading && paper && (
                                <button className="sub-action-btn sub-action-danger" onClick={handleDelete} disabled={deleting}>
                                    <Trash2 size={13} />
                                    {deleting ? "Deleting..." : "Delete Submission"}
                                </button>
                            )}

                        </div>
                </div>
            </main>

            {/* Score tooltip */}
            {openTooltip && scoreDescriptions[openTooltip] && (
                <div
                    className="score-tooltip"
                    style={{ top: tooltipPos.top, left: tooltipPos.left }}
                    onClick={e => e.stopPropagation()}
                >
                    {scoreDescriptions[openTooltip]}
                </div>
            )}

            {/* Share modal */}
            {shareModal && (
                <div className="share-modal-overlay" onClick={() => setShareModal(false)}>
                    <div className="share-modal-tile" onClick={e => e.stopPropagation()}>
                        <div className="share-modal-header">
                            <p className="share-modal-title">Share Report</p>
                            <button className="share-modal-close" onClick={() => setShareModal(false)}>×</button>
                        </div>
                        <div className="share-modal-body">
                            {shareStatus && (
                                <p className={shareStatus.type === "success" ? "chpw-success" : "chpw-error"} style={{ marginBottom: 12 }}>
                                    {shareStatus.msg}
                                </p>
                            )}
                            <form onSubmit={handleShare}>
                                <input
                                    type="email"
                                    className="share-email-input"
                                    placeholder="recipient@email.com"
                                    required
                                    value={shareEmail}
                                    onChange={e => setShareEmail(e.target.value)}
                                    disabled={sharing}
                                />
                                <button type="submit" className="auth-submit-btn" disabled={sharing} style={{ marginTop: 8 }}>
                                    {sharing ? "Sending..." : "Send Report"}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Peer Review submit modal */}
            {prModal && (
                <div className="share-modal-overlay" onClick={() => { if (!prSubmitting) setPrModal(false); }}>
                    <div className="share-modal-tile pr-submit-modal" onClick={e => e.stopPropagation()}>
                        <div className="share-modal-header">
                            <p className="share-modal-title">Submit for Peer Review</p>
                            <button className="share-modal-close" onClick={() => { if (!prSubmitting) setPrModal(false); }}>x</button>
                        </div>
                        <div className="share-modal-body">
                            <p className="pr-submit-desc">
                                Your paper will be added to the review queue and assigned to a qualified reviewer.
                            </p>
                            {prStatus && (
                                <p className={prStatus.type === "success" ? "chpw-success" : "chpw-error"} style={{ marginBottom: 12 }}>
                                    {prStatus.msg}
                                </p>
                            )}
                            <label className="upload-label">Notes for Reviewer <span style={{ color: "var(--ink-300)", fontWeight: 400 }}>(optional)</span></label>
                            <textarea
                                className="pr-submit-notes"
                                placeholder="Any specific areas you'd like the reviewer to focus on..."
                                value={prNotes}
                                onChange={e => setPrNotes(e.target.value)}
                                disabled={prSubmitting}
                                rows={3}
                            />
                            <button
                                className="auth-submit-btn"
                                disabled={prSubmitting}
                                style={{ marginTop: 12 }}
                                onClick={handlePrSubmit}
                            >
                                {prSubmitting ? "Submitting..." : "Submit for Peer Review"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
